import React from 'react';
import { platformMeta } from '../platforms';

function formatUptime(secs) {
  if (!secs) return '0:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${m}:${String(s).padStart(2,'0')}`;
}

const statusColor = (status) => ({
  live:       { bg: 'rgba(0,230,118,0.1)',  border: 'rgba(0,230,118,0.25)',  text: '#00e676' },
  connecting: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.25)', text: '#f59e0b' },
  error:      { bg: 'rgba(255,77,77,0.1)',  border: 'rgba(255,77,77,0.25)',  text: '#ff4d4d' },
  ended:      { bg: 'rgba(92,107,130,0.1)', border: 'rgba(92,107,130,0.25)', text: '#5c6b82' },
}[status] || { bg: 'var(--surface2)', border: 'var(--border)', text: 'var(--text-muted)' });

export default function LiveStatus({ status }) {
  const live = status?.live;
  const s = {
    banner: {
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px',
      padding: '16px 20px', borderRadius: 'var(--radius-lg)', marginBottom: '28px',
      border: `1px solid ${live ? 'rgba(0,230,118,0.2)' : 'var(--border)'}`,
      background: live ? 'rgba(0,230,118,0.05)' : 'var(--surface)',
      transition: 'all 0.3s',
    },
    indicator: {
      width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0,
      background: live ? 'var(--signal)' : 'var(--text-muted)',
      animation: live ? 'pulse-dot 1.4s infinite' : 'none',
    },
    label: { fontWeight: '700', fontSize: '15px' },
    uptime: { fontFamily: 'var(--mono)', fontSize: '13px', color: 'var(--text-muted)', marginLeft: '4px' },
    pills: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: 'auto' },
    pill: (st) => {
      const c = statusColor(st);
      return {
        display: 'flex', alignItems: 'center', gap: '5px',
        fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px',
        background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      };
    },
    platDot: (color) => ({ width: '6px', height: '6px', borderRadius: '50%', background: color }),
  };

  return (
    <div style={s.banner}>
      <div style={s.indicator} />
      <span style={s.label}>{live ? 'Live now' : 'Offline'}</span>
      {live && status.uptimeSeconds > 0 && (
        <span style={s.uptime}>{formatUptime(status.uptimeSeconds)}</span>
      )}
      {live && status.destinations?.length > 0 && (
        <div style={s.pills}>
          {status.destinations.map((d, i) => {
            const pm = platformMeta(d.platform);
            return (
              <div key={i} style={s.pill(d.status)}>
                <div style={s.platDot(pm.color)} />
                {d.label}
                {d.status === 'live' && ' ●'}
                {d.status === 'connecting' && ' ◌'}
                {d.status === 'error' && ' ✕'}
              </div>
            );
          })}
        </div>
      )}
      {!live && status?.lastSession && (
        <span style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--text-muted)' }}>
          Last live: {new Date(status.lastSession.started_at).toLocaleDateString()}
        </span>
      )}
    </div>
  );
}
